import { Box, Button, Flex, Tag, Text, useTheme } from "@chakra-ui/react";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, Roles } from "common";
import dayjs from "dayjs";
import { uniq } from "lodash";
import React, { useEffect, useState } from "react";
import {
  useGetAuditRecordsMutation,
  useGetListActivityTypeMutation,
} from "redux/services/auditRecord";
import {
  AuditRecord,
  AuditTimeLineData,
  GetAuditRecordRequest,
  Paging,
} from "redux/services/auditRecord/types";
import { constructFullName, dateFormat, FORMATS } from "utils";
import { useLocation } from "react-router-dom";
import qs from "qs";
import InfiniteScroll from "react-infinite-scroll-component";
import DatePicker from "components/DatePicker";
import AutoComplete from "components/AutoComplete";
import { TAutoCompleteOption } from "components/AutoComplete/AutoComplete.types";
import { useGetUsersQuery } from "redux/services/membership";
import { GetUsersRequest } from "redux/services/membership/types";
import { useRoles } from "hooks/useRoles";
import LoadingOverlay from "components/LoadingOverlay";

function Audit() {
  const theme = useTheme<any>();
  const { search } = useLocation();
  const { executedBy, resourceOwnerId }: any = qs.parse(
    search.replace("?", "")
  );
  const { isPaymentOpsRole } = useRoles();

  const DEFFAULT_PARAMS: GetAuditRecordRequest = {
    pageNumber: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    executedBy,
    resourceOwnerId,
    fromDateTime: dayjs()
      .subtract(1, "day")
      .startOf("day")
      .format(FORMATS["YYYY-MM-DDTHH:mm:ss"]),
    toDateTime: dayjs().endOf("days").format(FORMATS["YYYY-MM-DDTHH:mm:ss"]),
  };

  const DEFFAULT_PAGING: Paging = {
    pageNumber: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    totalRecords: undefined,
  };

  const [audiRecordPage, setAudiRecordPage] = useState<Paging>(DEFFAULT_PAGING);

  const [
    fetchAudiRecord,
    { data: auditRecords, isSuccess, isLoading: getAuditActivityLoading },
  ] = useGetAuditRecordsMutation();

  const [getActivityTypes, { data: activityTypes }] =
    useGetListActivityTypeMutation();

  const [totalAuditRecordLength, setTotalAuditRecordLength] = useState(0);
  const [, setActivityTypesOption] = useState<TAutoCompleteOption[]>([]);
  const [requestGetAuditParams, setGetAuditParamsRequest] =
    useState<GetAuditRecordRequest>(DEFFAULT_PARAMS);
  const [currentSelectActivityType, setCurrentSelectActivityType] =
    useState<TAutoCompleteOption[]>();
  const [auditRecordLocal, setAuditRecordLocal] = useState<AuditTimeLineData[]>(
    []
  );

  const isFirstFetchingAuditActivity =
    requestGetAuditParams.pageNumber === DEFAULT_PAGE &&
    getAuditActivityLoading;

  const [getUsersParamsRequest, setGetUsersParamsRequest] =
    useState<GetUsersRequest>({
      pageNum: DEFAULT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      listUserRoles: Roles.BusinessOwner,
    });
  const { data: usersData, isFetching: getUsersLoading } = useGetUsersQuery(
    getUsersParamsRequest,
    {
      skip: !isPaymentOpsRole,
    }
  );
  const [usersOptions, setUsersOptions] = useState<TAutoCompleteOption[]>([]);

  useEffect(() => {
    fetchMoreAudit();
    getActivityTypes({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isFirstFetching = getUsersParamsRequest.pageNum === DEFAULT_PAGE;
    const options =
      usersData?.data?.map((user) => ({
        label:
          constructFullName(user.firstName, undefined, user.lastName) ||
          user.userName,
        value: user.userId,
        data: user,
      })) ?? [];

    setUsersOptions(isFirstFetching ? options : [...usersOptions, ...options]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersData]);

  useEffect(() => {
    const options =
      activityTypes?.data?.map((activityType) => ({
        label: activityType,
        value: activityType,
        data: activityType,
      })) ?? [];

    setActivityTypesOption(options);
  }, [activityTypes]);

  useEffect(() => {
    if (isSuccess && auditRecords) {
      const uniqueDates: string[] = uniq(
        auditRecords?.data?.map(
          (record: AuditRecord) =>
            dateFormat(record.executedAt, true, FORMATS["YYYY-MM-DD"]) || ""
        )
      );
      if (uniqueDates) {
        const auditData: AuditTimeLineData[] = uniqueDates.map(
          (date: string) => {
            const auditDataFilter = auditRecords?.data?.filter(
              (record: AuditRecord) =>
                dateFormat(record.executedAt, true, FORMATS["YYYY-MM-DD"]) ===
                date
            );
            return {
              date,
              records: auditDataFilter,
            };
          }
        );
        const clonedAuditRecords = auditRecordLocal;
        auditData.forEach((audit) => {
          const localMatch = clonedAuditRecords.find(
            (local) => local.date === audit.date
          );
          if (localMatch) {
            localMatch.records = [...localMatch.records, ...audit.records];
          } else {
            clonedAuditRecords.push(audit);
          }
        });
        setAuditRecordLocal(clonedAuditRecords);
        setAudiRecordPage(auditRecords.paging);
        setTotalAuditRecordLength(
          totalAuditRecordLength + auditRecords.data.length
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const fetchFilterList = () => {
    const updatedParamRequest = { ...requestGetAuditParams, pageNumber: 1 };
    fetchAudiRecord({
      ...updatedParamRequest,
      fromDateTime: dayjs(updatedParamRequest.fromDateTime)
        .utc()
        .format(FORMATS["YYYY-MM-DDTHH:mm:ss"]),
      toDateTime: dayjs(updatedParamRequest.toDateTime)
        .utc()
        .format(FORMATS["YYYY-MM-DDTHH:mm:ss"]),
    });
    setGetAuditParamsRequest(updatedParamRequest);
    setAuditRecordLocal([]);
    setTotalAuditRecordLength(0);
  };

  const refreshList = () => {
    const updatedParamRequest = { ...DEFFAULT_PARAMS, pageNumber: 1 };
    fetchAudiRecord({
      ...updatedParamRequest,
      fromDateTime: dayjs(updatedParamRequest.fromDateTime)
        .utc()
        .format(FORMATS["YYYY-MM-DDTHH:mm:ss"]),
      toDateTime: dayjs(updatedParamRequest.toDateTime)
        .utc()
        .format(FORMATS["YYYY-MM-DDTHH:mm:ss"]),
    });
    setGetAuditParamsRequest(updatedParamRequest);
    setAuditRecordLocal([]);
    setTotalAuditRecordLength(0);
    setCurrentSelectActivityType([]);
  };

  const fetchMoreAudit = () => {
    const updatedParamRequest = {
      ...requestGetAuditParams,
      pageNumber: audiRecordPage.pageNumber + 1,
    };
    fetchAudiRecord({
      ...updatedParamRequest,
      fromDateTime: dayjs(updatedParamRequest.fromDateTime)
        .utc()
        .format(FORMATS["YYYY-MM-DDTHH:mm:ss"]),
      toDateTime: dayjs(updatedParamRequest.toDateTime)
        .utc()
        .format(FORMATS["YYYY-MM-DDTHH:mm:ss"]),
    });
    setGetAuditParamsRequest(updatedParamRequest);
  };

  const isHasMore =
    audiRecordPage.pageNumber * audiRecordPage.pageSize <
    (audiRecordPage.totalRecords || 100);

  return (
    <Flex w="100%" h="100%" p="20px" flexDir={"column"} alignItems="center">
      <Text alignSelf={"start"} textStyle={"h1"}>
        Audit Activity
      </Text>
      <Flex gap="20px" wrap="wrap" my="20px" w="100%">
        {isPaymentOpsRole && (
          <Box>
            <Text alignSelf={"start"} textStyle={"h4"} marginBottom="5px">
              Business Operation
            </Text>
            <AutoComplete
              placeholder="Requester"
              loading={getUsersLoading}
              options={usersOptions}
              maxW="200px"
              value={currentSelectActivityType}
              pageNumber={getUsersParamsRequest?.pageNum}
              pageSize={getUsersParamsRequest?.pageSize}
              total={usersData?.paging?.totalRecords}
              onChange={(option) => {
                const requesterId =
                  option.length > 0 ? option[0].data.userId : undefined;
                setGetAuditParamsRequest({
                  ...requestGetAuditParams,
                  executedBy: requesterId,
                });
                setCurrentSelectActivityType(option);
              }}
              onSearch={(keyword) => {
                setGetUsersParamsRequest({
                  ...getUsersParamsRequest,
                  pageNum: DEFAULT_PAGE,
                  searchText: keyword,
                });
              }}
              onLoadMore={() => {
                setGetUsersParamsRequest({
                  ...getUsersParamsRequest,
                  pageNum: getUsersParamsRequest.pageNum + 1,
                });
              }}
              w="200px"
            />
          </Box>
        )}
        <Box className="Audit-datefrom">
          <Text alignSelf={"start"} textStyle={"h4"} marginBottom="5px">
            Date From
          </Text>
          <DatePicker
            selected={
              requestGetAuditParams?.fromDateTime
                ? dayjs(requestGetAuditParams?.fromDateTime).toDate()
                : undefined
            }
            onChange={(date) => {
              const fromDateTime = dayjs(date).startOf("day");
              const toDateTime = dayjs(requestGetAuditParams.toDateTime).endOf(
                "day"
              );

              const isSelectDateAfterToDate =
                fromDateTime.valueOf() >= toDateTime.valueOf();

              setGetAuditParamsRequest({
                ...requestGetAuditParams,
                fromDateTime: fromDateTime.format(
                  FORMATS["YYYY-MM-DDTHH:mm:ss"]
                ),
                toDateTime: isSelectDateAfterToDate
                  ? fromDateTime
                    .clone()
                    .add(1, "day")
                    .endOf("day")
                    .format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
                  : requestGetAuditParams?.toDateTime,
              });
            }}
            width={isPaymentOpsRole ? "200px" : "250px"}
          />
        </Box>
        <Box className="Audit-dateto">
          <Text alignSelf={"start"} textStyle={"h4"} marginBottom="5px">
            Date To
          </Text>
          <DatePicker
            data-testid="endOfDay"
            width={isPaymentOpsRole ? "200px" : "250px"}
            selected={
              requestGetAuditParams?.toDateTime
                ? dayjs(requestGetAuditParams?.toDateTime).toDate()
                : undefined
            }
            minDate={
              requestGetAuditParams?.fromDateTime
                ? dayjs(requestGetAuditParams?.fromDateTime).toDate()
                : undefined
            }
            onChange={(date) => {
              const toDateTime = dayjs(date).endOf("day");

              setGetAuditParamsRequest({
                ...requestGetAuditParams,
                toDateTime: toDateTime.format(FORMATS["YYYY-MM-DDTHH:mm:ss"]),
              });
            }}
          />
        </Box>
        <Button
          data-testid="filter-btn"
          variant="solid"
          colorScheme="blue"
          fontWeight="600"
          marginTop="19px"
          fontSize="14px"
          onClick={fetchFilterList}
        >
          Filter
        </Button>
        <Button
          data-testid="refresh-btn"
          variant="solid"
          colorScheme="gray"
          fontWeight="600"
          marginTop="19px"
          fontSize="14px"
          onClick={refreshList}
        >
          Clear
        </Button>
      </Flex>

      {isFirstFetchingAuditActivity ? (
        <LoadingOverlay height="65vh" />
      ) : (
        <Flex w="100%" mt="20px">
          <InfiniteScroll
            style={{ width: "100%" }}
            dataLength={totalAuditRecordLength} //This is important field to render the next data
            next={fetchMoreAudit}
            hasMore={isHasMore}
            loader={
              <Text w="100%" textAlign="center">
                Loading...
              </Text>
            }
            endMessage={
              <p style={{ textAlign: "center" }}>
                <b>No more records to display</b>
              </p>
            }
            // below props only if you need pull down functionality
            refreshFunction={refreshList}
            scrollThreshold={0.9}
          >
            {auditRecordLocal.map((n) => (
              <Flex
                key={n.date}
                flexDir={"row"}
                minH="31px"
                align="start"
                w={"100%"}
                h="100%"
              >
                <Flex
                  alignItems={"center"}
                  flex="0 1 0%"
                  flexDir={"column"}
                  h="100%"
                  w={"100%"}
                >
                  <Flex
                    borderStyle="solid"
                    borderWidth={"2px"}
                    padding="4px"
                    borderRadius={"50%"}
                    borderColor={"transparent"}
                    color="rgb(250, 250, 250)"
                    backgroundColor={"rgb(189, 189, 189)"}
                    margin="11.5px 0px"
                    w={"100%"}
                  />

                  <Text
                    w={"2px"}
                    backgroundColor="rgb(189, 189, 189)"
                    flexGrow={1}
                    as={"span"}
                  />
                </Flex>
                <Flex w={"100%"}>
                  <Box minW={"100px"} ml="20px" mt="5.25px">
                    <Tag
                      size={"md"}
                      variant="main"
                      bgColor={theme?.colors?.main}
                      color="white"
                    >
                      {n?.date}
                    </Tag>
                  </Box>
                  <Flex
                    flexDir={"column"}
                    gap="20px"
                    ml={"20px"}
                    pb="20px"
                    w={"100%"}
                  >
                    {n?.records
                      .filter((child) => child?.eventName)
                      .map((child) => (
                        <Flex
                          key={child.id}
                          p="16px"
                          borderRadius={"10px"}
                          border="1px solid rgb(224, 224, 224)"
                          boxShadow={
                            "0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -2px rgba(0,0,0,.05)"
                          }
                          maxW="650px"
                        >
                          <Flex w={"100%"} justify={"space-between"} pos="relative">
                            <Flex
                              w={"100%"}
                              justify={"space-between"}
                              direction={"column"}
                            >
                              <Text fontWeight={"600"} fontSize="14px">
                                {child?.eventName}
                              </Text>
                              {child?.eventDescription && (
                                <Text
                                  fontWeight={"600"}
                                  fontSize="14px"
                                  marginTop={"10px"}
                                >
                                  {child?.eventDescription.replace(
                                    "File name: ",
                                    ""
                                  )}
                                </Text>
                              )}
                              {child?.executedByName && (
                                <Text
                                  fontWeight={"400"}
                                  fontSize="12px"
                                  marginTop={"10px"}
                                  color="gray.500"
                                >
                                  {child?.executedByName}
                                </Text>
                              )}
                            </Flex>
                            <Text
                              fontWeight={"light"}
                              fontSize="12px"
                              whiteSpace={"nowrap"}
                              pos="absolute"
                              top="0"
                              right="0"
                            >
                              {dateFormat(child?.executedAt, true)}
                            </Text>
                          </Flex>
                        </Flex>
                      ))}
                  </Flex>
                </Flex>
              </Flex>
            ))}
            {auditRecordLocal.length === 0 && (
              <Text w="100%" textAlign="center" fontWeight="bold">
                No records found
              </Text>
            )}
          </InfiniteScroll>
        </Flex>
      )}
    </Flex>
  );
}

export default Audit;
